import type { Dispatch, SetStateAction } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import type { NavigateFunction } from 'react-router-dom';
import posthog from 'posthog-js';
import { resetHouseAbsenceMapObjectsMapObjectIdResetAbsencePost } from '../../client';
import type { MapObject, OwnerInfo } from '../../models/mapObject';
import { showAlert } from '../../utils/telegram/alert';
import { cn } from '../cn';
import { HOUSE_ABANDON_DAYS, HOUSE_ABANDON_GRACE_DAYS } from '../../domain/house';


export interface AbandonmentBarProps {
  plot: MapObject;
  owner: OwnerInfo | null;
  isOwner: string | boolean | null | undefined;
  showAbandonmentTooltip: boolean;
  setShowAbandonmentTooltip: Dispatch<SetStateAction<boolean>>;
  showVipProtectionTooltip: boolean;
  setShowVipProtectionTooltip: Dispatch<SetStateAction<boolean>>;
  showHeritageProtectionTooltip: boolean;
  setShowHeritageProtectionTooltip: Dispatch<SetStateAction<boolean>>;
  resetAbsenceLoading: boolean;
  setResetAbsenceLoading: Dispatch<SetStateAction<boolean>>;
  queryClient: QueryClient;
  onClose: () => void;
  navigate: NavigateFunction;
  t: TFunction;
}

export default function AbandonmentBar({
  plot,
  owner,
  isOwner,
  showAbandonmentTooltip,
  setShowAbandonmentTooltip,
  showVipProtectionTooltip,
  setShowVipProtectionTooltip,
  showHeritageProtectionTooltip,
  setShowHeritageProtectionTooltip,
  resetAbsenceLoading,
  setResetAbsenceLoading,
  queryClient,
  onClose,
  navigate,
  t,
}: AbandonmentBarProps) {
  const ABANDONMENT_LIMIT = HOUSE_ABANDON_DAYS;
  const ABANDONMENT_GRACE_DAYS = HOUSE_ABANDON_GRACE_DAYS;
  const ownerIsVip = owner?.vip_until ? new Date(owner.vip_until) > new Date() : false;
  const isHeritage = Boolean(plot.data?.heritage);
  const isAbandonmentProtected = ownerIsVip || isHeritage;
  // Use absence_reset_at (only updated by cron/button), fall back to last_active_at for old users
  const resetStr = (plot.data?.owner_absence_reset_at ?? plot.data?.owner_last_active_at) as string | undefined;
  const resetDate = resetStr ? new Date(resetStr) : null;
  // Day-granularity snapshot for display; re-renders on prop change, not on a timer, so the exact
  // ms returned by Date.now() is irrelevant (two calls in the same render land on the same day).
  // eslint-disable-next-line react-hooks/purity -- day-granularity display only; no timer re-render
  const actualDays = resetDate ? Math.floor((Date.now() - resetDate.getTime()) / 86400000) : 0;
  // Grace period: first 2 days show 0, count starts from day 3
  const daysSinceVisit = isAbandonmentProtected ? 0 : Math.min(ABANDONMENT_LIMIT, Math.max(0, actualDays - ABANDONMENT_GRACE_DAYS));
  const progress = isAbandonmentProtected ? 0 : Math.min(daysSinceVisit / ABANDONMENT_LIMIT, 1);
  const barColor = progress < 0.5 ? 'var(--color-brand)' : progress < 0.8 ? 'var(--color-abandonment-bar-text)' : 'var(--color-abandonment-bar-text-2)';
  return (
    <>
      {/* Overlay to close tooltip on outside click */}
      {showAbandonmentTooltip && (
        <div
          className="fixed inset-0 z-[998]"
          onClick={() => setShowAbandonmentTooltip(false)}
        />
      )}
      <div className="relative mb-xs w-full">
        <div className="mb-sm flex items-center gap-sm">
          <span className="[font-family:Lato,sans-serif] text-[12px] font-normal text-white-alpha-60">
            {t('House abandoned for days:')}
          </span>
          <span
            className="relative z-[999] inline-flex h-xl w-xl shrink-0 cursor-pointer items-center justify-center rounded-full border border-white-alpha-40 text-[10px] text-white-alpha-60"
            onClick={(e) => { e.stopPropagation(); setShowAbandonmentTooltip(v => !v); }}
          >
            ?
          </span>
          {/* Reset button — right-aligned in the label row, only for owner (not VIP/heritage, no days to reset) */}
          {isOwner && !isAbandonmentProtected && (
            <button
              disabled={resetAbsenceLoading || daysSinceVisit === 0}
              onClick={async () => {
                posthog.capture('house_absence_reset_clicked', {
                  house_id: plot.id,
                  days_since_visit: daysSinceVisit,
                });
                setResetAbsenceLoading(true);
                try {
                  await resetHouseAbsenceMapObjectsMapObjectIdResetAbsencePost({
                    path: { map_object_id: plot.id },
                    throwOnError: true,
                  });
                  await queryClient.invalidateQueries({ queryKey: ['mapObjects'] });
                } catch {
                  showAlert(t('Failed to reset absence'));
                } finally {
                  setResetAbsenceLoading(false);
                }
              }}
              className={cn(
                'ml-auto h-2xl shrink-0 whitespace-nowrap rounded-[5px] px-md [font-family:Lato,sans-serif] text-[11px] font-semibold',
                daysSinceVisit === 0
                  ? 'border border-white-alpha-12 bg-white-alpha-5 text-white-alpha-30'
                  : 'border border-brand-alpha-40 bg-brand-alpha-15 text-brand',
                (resetAbsenceLoading || daysSinceVisit === 0) ? 'cursor-not-allowed' : 'cursor-pointer',
                resetAbsenceLoading ? 'opacity-60' : 'opacity-100',
              )}
            >
              {resetAbsenceLoading ? '...' : t('Reset')}
            </button>
          )}
          {showAbandonmentTooltip && (
            <div
              className="absolute top-[28px] right-0 left-0 z-[999] rounded-sm border border-brand-alpha-40 bg-popover-surface-alpha-97 px-lg py-md [font-family:Lato,sans-serif] text-[12px] leading-[1.5] text-white"
              onClick={() => setShowAbandonmentTooltip(false)}
            >
              {t('The indicator counts days since the owner last opened the game. If it reaches {{days}} days, the house will be lost. The counter automatically resets for all houses when the owner returns to the game, or instantly by pressing the reset button.', { days: HOUSE_ABANDON_DAYS })}
            </div>
          )}
        </div>
        <div className="relative">
          {(showVipProtectionTooltip || showHeritageProtectionTooltip) && (
            <div
              className="fixed inset-0 z-[998]"
              onClick={() => {
                setShowVipProtectionTooltip(false);
                setShowHeritageProtectionTooltip(false);
              }}
            />
          )}
          <div
            className={cn(
              'relative h-[18px] w-full overflow-hidden rounded-[9px]',
              isAbandonmentProtected
                ? 'cursor-pointer border border-reward-alpha-35 bg-reward-alpha-15'
                : 'cursor-default border-none bg-white-alpha-10',
            )}
            onClick={
              isHeritage
                ? (e) => {
                    e.stopPropagation();
                    setShowHeritageProtectionTooltip(v => !v);
                    setShowVipProtectionTooltip(false);
                  }
                : ownerIsVip
                  ? (e) => {
                      e.stopPropagation();
                      setShowVipProtectionTooltip(v => !v);
                    }
                  : undefined
            }
          >
            {!isAbandonmentProtected && (
              <div
                className="absolute top-0 bottom-0 left-0 rounded-[9px] [transition:width_0.3s_ease]"
                style={{
                  width: `${progress * 100}%`,
                  background: barColor,
                }}
              />
            )}
            <div
              className={cn(
                'absolute inset-0 flex items-center justify-center gap-xs [font-family:Lato,sans-serif] text-[11px] font-bold tracking-[0.3px]',
                isAbandonmentProtected ? 'text-gold' : 'text-white',
              )}
            >
              {isHeritage ? (
                <span className="flex items-center gap-xs">
                  <img src="/ui/shield-icon.svg" alt="" className="h-[14px] w-[14px]" />
                  {t('Official Web3Islands landmark')}
                </span>
              ) : ownerIsVip ? (
                <span>⭐ VIP</span>
              ) : (
                <>
                  <span className="[direction:ltr] [unicode-bidi:isolate]">
                    {daysSinceVisit} / {ABANDONMENT_LIMIT}
                  </span>
                  <span>{t('days')}</span>
                </>
              )}
            </div>
          </div>
          {showVipProtectionTooltip && ownerIsVip && !isHeritage && (
            <div
              className="absolute top-3xl right-0 left-0 z-[999] rounded-sm border border-reward-alpha-40 bg-popover-surface-alpha-97 px-lg py-md [font-family:Lato,sans-serif] text-[12px] leading-[1.5] text-white"
              onClick={() => setShowVipProtectionTooltip(false)}
            >
              {t('VIP players never lose their houses, even if they don\'t log in for a long time.')}
              {' '}
              <span
                className="cursor-pointer text-gold underline"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowVipProtectionTooltip(false);
                  onClose();
                  navigate('/shop?category=vip');
                }}
              >
                {t('Learn more')}
              </span>
            </div>
          )}
          {showHeritageProtectionTooltip && isHeritage && (
            <div
              className="absolute top-3xl right-0 left-0 z-[999] rounded-sm border border-reward-alpha-40 bg-popover-surface-alpha-97 px-lg py-md [font-family:Lato,sans-serif] text-[12px] leading-[1.5] text-white"
              onClick={() => setShowHeritageProtectionTooltip(false)}
            >
              <div className="mb-xs font-bold">{t('Why is this an island landmark?')}</div>
              {t('This house received a special status for its exceptional design and attention to detail. The owner invested a lot of time and effort, so we preserved it for more players to see this work.')}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
